import { createApi } from '@reduxjs/toolkit/query/react';
import { PaymentType, formatDate } from 'core';

import { axiosBaseQuery } from '../axiosBaseQuery';

export interface PaymentDetails {
  currency: string;
  fullFee: number;
  isPaid: boolean;
  paymentSlipNumber: string;
  reference: string;
  sessionStageChanged: boolean;
  taxRate: number;
  onlinePaymentFee?: number;
  offlinePaymentFee?: number;
}

export interface PaymentDetailsResponse {
  Currency: string;
  FullFee: number;
  IsPaid: boolean;
  PaymentSlipNumber: string;
  Reference: string;
  SessionStageChanged: boolean;
  TaxRate: number;
  OnlinePaymentFee?: number;
  OfflinePaymentFee?: number;
}

export interface OrganisationCountrySettingsResponse {
  AllowHybridJourney: unknown;
  AllowOfflinePayment: boolean;
  AllowOnlinePayment: boolean;
  AllowedPaymentDescriptions: { ShortCode: string; Header: string; Id: number; Description: string }[];
  Country: unknown;
  CountryId: number;
  FacebookLink: unknown;
  Guid: string;
  Id: number;
  OrganisationCountrySchoolsSpecific: unknown;
  Schools: unknown;
  TwitterLink: unknown;
  URL: unknown;
}

export interface OrganisationCountryPaymentSettings {
  allowOfflinePayment: boolean;
  allowOnlinePayment: boolean;
  allowedPaymentDescriptions: { shortCode: string; header: string; id: number; description: string }[];
  countryId: number;
}

export interface CandidateExamDataResponse {
  Candidate: { Id: number; Email: string } | undefined;
  CandidateExams: {
    Components: string[];
    ComponentsDate: Array<string | null>;
    SyllabusName: string;
    SubjectName: string;
    DistrictName: string;
    PreparationCentreName: string;
    SessionName: string;
    Fee: number;
    CurrencyCode: string;
    RegistrationGroupReference: string;
    TaxRate: number;
    OnlinePaymentFee: number;
    OfflinePaymentFee: number;
  }[];
}

export interface CandidateExamData {
  exams: Exam[];
  candidateId: number | undefined;
  candidateEmail: string;
}

export interface Exam {
  components: string[];
  syllabusName: string;
  subjectName: string;
  districtName: string;
  preparationCentreName: string;
  sessionName: string;
  fee: number;
  currencyCode: string;
  registrationGroupReference: string;
  taxRate: number;
  onlinePaymentFee: number;
  offlinePaymentFee: number;
}

export const paymentApi = createApi({
  reducerPath: 'paymentApi',
  baseQuery: axiosBaseQuery({
    baseUrl: '/web-api/api',
  }),
  endpoints: (builder) => ({
    getPaymentDetails: builder.query<PaymentDetails, string>({
      query: (referenceNumber) => ({
        url: `/payment/getPaymentDetails`,
        method: 'post',
        data: {
          TransactionReference: referenceNumber,
        },
      }),
      transformResponse: (res: PaymentDetailsResponse) => ({
        currency: res.Currency,
        fullFee: res.FullFee,
        isPaid: res.IsPaid,
        paymentSlipNumber: res.PaymentSlipNumber,
        reference: res.Reference,
        sessionStageChanged: res.SessionStageChanged,
        taxRate: res.TaxRate,
        onlinePaymentFee: res.OnlinePaymentFee,
        offlinePaymentFee: res.OfflinePaymentFee,
      }),
    }),
    getOrganisationCountryPaymentSettings: builder.query<OrganisationCountryPaymentSettings, string>({
      query: (referenceNumber) => ({
        url: `/country/getOrganisationCountryByRegistrationGroup?RegistrationGroupReference=${referenceNumber}`,
        method: 'get',
        data: {
          TransactionReference: referenceNumber,
        },
      }),
      transformResponse: (res: OrganisationCountrySettingsResponse) => ({
        allowOfflinePayment: res.AllowOfflinePayment,
        allowOnlinePayment: res.AllowOnlinePayment,
        allowedPaymentDescriptions:
          res.AllowedPaymentDescriptions?.map((x) => ({
            shortCode: x.ShortCode,
            id: x.Id,
            description: x.Description,
            header: x.Header,
          })) ?? [],
        countryId: res.CountryId,
      }),
    }),
    payOffline: builder.mutation<
      void,
      { paymentType: PaymentType; referenceNumber: string; preparationCentreName: string | undefined }
    >({
      query: (data) => ({
        url: `/payment/offline`,
        method: 'post',
        data: {
          PaymentType: data.paymentType,
          TransactionReference: data.referenceNumber,
          PreparationCentreName: data.preparationCentreName,
        },
      }),
    }),
    payOnline: builder.mutation<string, { paymentType: PaymentType; referenceNumber: string }>({
      query: (data) => ({
        url: `/payment/online`,
        method: 'post',
        data: {
          PaymentType: data.paymentType,
          TransactionReference: data.referenceNumber,
        },
      }),
      transformResponse: (res: { RedirectUrl: string }) => res.RedirectUrl,
    }),
    getDistrict: builder.query<{ id: number; name: string }, string>({
      query: (referenceNumber) => ({
        url: `/country/getDistrictByRegistrationGroup?registrationGroupReference=${referenceNumber}`,
        method: 'get',
      }),
      transformResponse: (res: { Id: number; Name: string }) => ({ id: res.Id, name: res.Name }),
    }),
    getCandidateExamData: builder.query<CandidateExamData, string>({
      query: (referenceNumber) => ({
        url: `/examregistation/b2c/getCandidateExamData/details`,
        method: 'post',
        data: {
          referenceNumber: referenceNumber,
        },
      }),
      transformResponse: (res: CandidateExamDataResponse) => ({
        candidateId: res.Candidate?.Id,
        candidateEmail: res.Candidate?.Email ?? '',
        exams: res.CandidateExams.map((exam) => ({
          components: exam.Components.map((component, i) => {
            const date = exam.ComponentsDate[i];
            return `${component} - ${date ? formatDate(date) : 'TBC'}`;
          }),
          syllabusName: exam.SyllabusName,
          subjectName: exam.SubjectName,
          districtName: exam.DistrictName,
          preparationCentreName: exam.PreparationCentreName,
          sessionName: exam.SessionName,
          fee: exam.Fee,
          onlinePaymentFee: exam.OnlinePaymentFee,
          offlinePaymentFee: exam.OfflinePaymentFee,
          currencyCode: exam.CurrencyCode,
          registrationGroupReference: exam.RegistrationGroupReference,
          taxRate: exam.TaxRate,
        })),
      }),
    }),
  }),
});

export const {
  useGetPaymentDetailsQuery,
  useGetOrganisationCountryPaymentSettingsQuery,
  usePayOfflineMutation,
  usePayOnlineMutation,
  useGetDistrictQuery,
  useGetCandidateExamDataQuery,
} = paymentApi;
